// frontend/src/components/TechnicalCard/SheetClassifier.jsx

import { useState } from 'react'
import { Button } from '../Common/Button'
import { Modal } from '../Common/Modal'
import { Check, X, Edit2, Zap } from 'lucide-react'
import toast from 'react-hot-toast'

export const SheetClassifier = ({ sheet, onClassify, onAnalyze, loading, hideAnalyzeButton = false }) => {
  const [showModal, setShowModal] = useState(false)
  const [selectedType, setSelectedType] = useState(sheet.sheet_type)

  const sheetTypes = [
    { value: 'materials', label: 'Материалы', color: 'bg-blue-100 text-blue-800' },
    { value: 'assembly', label: 'Сборка', color: 'bg-green-100 text-green-800' },
    { value: 'operational', label: 'Операции', color: 'bg-purple-100 text-purple-800' },
    { value: 'unknown', label: 'Неизвестно', color: 'bg-gray-100 text-gray-800' },
  ]

  const handleConfirm = async () => {
    if (selectedType !== sheet.sheet_type) {
      await onClassify(sheet.id, selectedType)
    }
    setShowModal(false)
  }

  const handleAnalyze = async () => {
    if (onAnalyze) {
      await onAnalyze(sheet.id)
    }
  }

  const isClassified = sheet.sheet_type && sheet.sheet_type !== 'unknown'
  const canAnalyze = isClassified && sheet.status !== 'analyzing' && sheet.status !== 'analyzed'
  const currentType = sheetTypes.find(t => t.value === sheet.sheet_type)

  // Получаем URL изображения из S3
  const getImageUrl = () => {
    // Приоритет: presigned URL > прокси через backend
    if (sheet.image_url) {
      return sheet.image_url
    }
    // Fallback: прокси через backend
    return `/api/technical_cards/${sheet.id}/image`
  }

  return (
    <div className={`bg-white rounded-lg shadow overflow-hidden transition-all duration-200 ${
      isClassified ? 'border-l-4 border-l-green-500' : ''
    } ${
      sheet.status === 'analyzed' ? 'ring-2 ring-green-500' : ''
    }`}>
      <div className="aspect-video bg-gray-200 flex items-center justify-center relative">
        <img
          src={getImageUrl()}
          alt={`Page ${sheet.page_number}`}
          className="w-full h-full object-contain"
          onError={(e) => {
            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2Y1ZjVmNSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBub3QgZm91bmQ8L3RleHQ+PC9zdmc+'
          }}
          loading="lazy"
          crossOrigin="anonymous"
        />
        
        {/* Индикатор статуса */}
        {sheet.status === 'analyzed' && (
          <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded text-xs font-semibold">
            ✓ Анализирован
          </div>
        )}
        {sheet.status === 'analyzing' && (
          <div className="absolute top-2 right-2 bg-blue-500 text-white px-2 py-1 rounded text-xs font-semibold animate-pulse">
            ⟳ Анализ...
          </div>
        )}
      </div>

      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-sm text-gray-600">Страница {sheet.page_number}</p>
            <p className={`font-semibold px-2 py-1 rounded text-sm inline-block mt-1 ${currentType?.color || 'bg-gray-100 text-gray-800'}`}>
              {currentType?.label || 'Не классифицирован'}
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowModal(true)}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2"
          >
            <Edit2 size={16} />
            {loading ? 'Сохранение...' : 'Выбрать тип'}
          </Button>

          {/* Кнопка анализа */}
          {!hideAnalyzeButton && canAnalyze && (
            <Button
              variant="secondary"
              size="sm"
              onClick={handleAnalyze}
              disabled={loading || sheet.status === 'analyzing'}
              className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-purple-500 to-blue-500 text-white hover:from-purple-600 hover:to-blue-600"
            >
              <Zap size={16} />
              {sheet.status === 'analyzing' ? 'Анализируется...' : 'Анализировать'}
            </Button>
          )}

          {sheet.status === 'analyzed' && (
            <div className="text-xs text-green-600 text-center font-medium">
              ✓ Данные извлечены
            </div>
          )}
        </div>
      </div>

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="Выбрать тип листа"
      >
        <div className="space-y-3">
          {sheetTypes.map(({ value, label, color }) => (
            <label 
              key={value} 
              className="flex items-center p-4 border-2 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors" 
              style={{
                borderColor: selectedType === value ? '#2563eb' : '#e5e7eb'
              }}
            >
              <input
                type="radio"
                name="sheet-type"
                value={value}
                checked={selectedType === value}
                onChange={(e) => setSelectedType(e.target.value)}
                className="mr-3 w-4 h-4"
              />
              <span className={`font-medium px-3 py-1 rounded text-sm ${color}`}>
                {label}
              </span>
            </label>
          ))}
        </div>

        <div className="flex gap-2 justify-end mt-6">
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Отмена
          </Button>
          <Button variant="primary" onClick={handleConfirm}>
            Подтвердить
          </Button>
        </div>
      </Modal>
    </div>
  )
}
